import type { ReactElement } from 'react';

import type { DrupalBlock } from '@commons/types/drupal';

import AccordionBlock from './Accordion/Accordion';
import ImagesAccordionBlock from './Accordion/ImagesAccordion';
import AnimatedHeadlineBlock from './AnimatedHeadline';
import AudioBlock from './Audio';
import BreadCrumbsBlock from './BreadCrumbs';
import ButtonBlock from './Button';
import CardBlock from './Card';
import CarouselBlock from './Carousel';
import ClickableTileBlock from './ClickableTile';
import CollapsibleBlock from './Collapsible';
import FeatureBandBlock from './FeatureBand';
import IframeBlock from './Iframe';
import { ImageInlineBlock } from './Image';
import ImageRevealBlock from './ImageReveal';
import ArticleHeaderMetadataBlock from './Insights/ArticleHeaderMetadata';
import InsightDownloadButton from './Insights/DownloadButton';
import InsightFeaturedImageBlock from './Insights/FeaturedImage';
import InsightIntroductionBlock from './Insights/Introduction';
import InsightSectionsBlock from './Insights/Sections';
import InsightSidebarBlock from './Insights/Sidebar';
import InsightTitle from './Insights/Title';
import InsightTopicsListBlock from './Insights/TopicsList';
import InsightWebinarWatchButton from './Insights/WebinarWatchButton';
import LinksList from './LinksList';
import NavigationBlock from './Navigation';
import NestedSectionBlock from './NestedSection';
import NodeBlock from './Node';
import PageDescriptionFieldBlock from './Page/Description';
import PageImageFieldBlock from './Page/Image';
import PageTitleFieldBlock from './Page/Title';
import ResponsiveImageBlock from './ResponsiveImage';
import RichTextBlock from './RichText';
import ScrollableHeadlineBlock from './ScrollableHeadline';
import ScrollableSlideBlock from './ScrollableSlide';
import SeparatorBlock from './Separator';
import ShareLinksBlock from './ShareLinks';
import SiteAlertsTitleFieldBlock from './SiteAlert/Title';
import SpacerBlock from './Spacer';
import SurveyMonkeyBlock from './SurveyMonkey';
import TabbedContentBlock from './TabbedContent';
import TermBlock from './Term';
import TexturedTileBlock from './TexturedTile';
import TileBlock from './Tile';
import TitleBlock from './Title';
import LocaleVideoBlock from './Video/Local';
import RemoteVideoBlock from './Video/Remote';
import ViewReferenceBlock from './ViewReference';
import WebformBlock from './Webform';

/**
 * Enum of all available inline block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum INLINE_BLOCK_BUNDLE {
  Card = 'inline_block:card',
  ClickableTile = 'inline_block:tile',
  Title = 'inline_block:title',
  Image = 'inline_block:media',
  ResponsiveImage = 'inline_block:responsive_image',
  Audio = 'inline_block:audio',
  Spacer = 'inline_block:spacer',
  Webform = 'inline_block:webform',
  ShareLinks = 'inline_block:share',
  LocalVideo = 'inline_block:video',
  Carousel = 'inline_block:carousel',
  RichText = 'inline_block:rich_text',
  Separator = 'inline_block:separator',
  Iframe = 'inline_block:iframe_block',
  Accordion = 'inline_block:accordion',
  LinksList = 'inline_block:links_list',
  Button = 'inline_block:call_to_action',
  Navigation = 'inline_block:navigation',
  Collapsible = 'inline_block:collapsible',
  Tile = 'inline_block:icon_card',
  RemoteVideo = 'inline_block:remote_video',
  TexturedTile = 'inline_block:textured_tile',
  TabbedContent = 'inline_block:tabbed_content',
  ViewReference = 'inline_block:views_reference',
  NestedSection = 'inline_block:nested_section',
  ScrollableSlide = 'inline_block:scrollable_slide',
  ImageReveal = 'inline_block:image_reveal',
  ImagesAccordion = 'inline_block:accordion_preview',
  AnimatedHeadline = 'inline_block:animated_headline',
  ContentReference = 'inline_block:content_reference',
  BreadCrumbsManual = 'inline_block:breadcrumbs_manual',
  ScrollableHeadline = 'inline_block:scrollable_headline',
  FeatureBand = 'inline_block:featureband',
  SurveyMonkey = 'inline_block:survey_monkey',
}

/**
 * Enum of all available entity block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum ENTITY_BLOCK_BUNDLE {
  Node = 'entity_block:node',
  Term = 'entity_block:taxonomy_term',
}

/**
 * Enum of all available fixed block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum FIXED_BLOCK_BUNDLE {}

/**
 * Enum of all available plugin block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum PLUGIN_BLOCK_BUNDLE {
  ArticleHeaderMetadata = 'crisis24_article_metadata',
  InsightSideBar = 'crisis24_insights_metadata',
}

/**
 * Enum of all available field block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum FIELD_BLOCK_BUNDLE {
  PageTitle = 'field_block:node:landing_page:title',
  PageDescription = 'field_block:node:landing_page:body',
  PageImage = 'field_block:node:landing_page:field_image',

  CampaignPageTitle = 'field_block:node:campaign_page:title',
  CampaignPageDescription = 'field_block:node:campaign_page:body',
  CampaignPageImage = 'field_block:node:campaign_page:field_image',

  IntelligenceTitle = 'field_block:node:intelligence:title',
  IntelligenceDescription = 'field_block:node:intelligence:body',
  IntelligenceImage = 'field_block:node:intelligence:field_image',

  ArticleTitle = 'field_block:node:article:title',
  ArticleSections = 'field_block:node:article:field_insight_sections',
  ArticleFeaturedImage = 'field_block:node:article:field_image',
  ArticleIntroduction = 'field_block:node:article:body',
  ArticleInsightsTopics = 'field_block:node:article:field_insight_topics',

  CaseStudyTitle = 'field_block:node:case_study:title',
  CaseStudySections = 'field_block:node:case_study:field_insight_sections',
  CaseStudyFeaturedImage = 'field_block:node:case_study:field_image',
  CaseStudyIntroduction = 'field_block:node:case_study:body',
  CaseStudyInsightsTopics = 'field_block:node:case_study:field_insight_topics',
  CaseStudyFileDownloadBtn = 'field_block:node:case_study:field_file',

  EventTitle = 'field_block:node:event:title',
  EventSections = 'field_block:node:event:field_insight_sections',
  EventFeaturedImage = 'field_block:node:event:field_image',
  EventIntroduction = 'field_block:node:event:body',
  EventInsightsTopics = 'field_block:node:event:field_insight_topics',

  PodcastTitle = 'field_block:node:podcast:title',
  PodcastSections = 'field_block:node:podcast:field_insight_sections',
  PodcastFeaturedImage = 'field_block:node:podcast:field_image',
  PodcastIntroduction = 'field_block:node:podcast:body',
  PodcastInsightsTopics = 'field_block:node:podcast:field_insight_topics',

  WhitePaperTitle = 'field_block:node:white_paper:title',
  WhitePaperFeaturedImage = 'field_block:node:white_paper:field_image',
  WhitePaperIntroduction = 'field_block:node:white_paper:body',
  WhitePaperInsightsTopics = 'field_block:node:white_paper:field_insight_topics',
  WhitePaperFileDownloadBtn = 'field_block:node:white_paper:field_file',

  WebinarTitle = 'field_block:node:webinar:title',
  WebinarFeaturedImage = 'field_block:node:webinar:field_image',
  WebinarContent = 'field_block:node:webinar:field_introduction',
  WebinarInsightsTopics = 'field_block:node:webinar:field_insight_topics',
  WebinarWatchButton = 'field_block:node:webinar:field_webinar_url',

  SiteAlertTitle = 'field_block:node:site_alert:title',
  SiteAlertBody = 'field_block:node:site_alert:body',
}

/**
 * Enum of all available system block bundles.
 *
 * Makes it easier to read and edit the ids.
 */

export enum SYSTEM_BLOCK_BUNDLE {
  BreadCrumbs = 'system_breadcrumb_block',
}

/**
 * Type definition for a block component namespace.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Block<Props = DrupalBlock<any>> = {
  (props: Props): ReactElement | null;

  /**
   * Flag to indicate whether the block supports animation or not.
   *
   * By default, all blocks support animation unless specified otherwise.
   * This is useful for blocks that have their own animation logic.
   *
   * IMPORTANT: When setting this to false, the default export should be a server component.
   *
   * @example
   *
   * const BlockWithoutAnimation = () => <div>My Block</div>;
   *
   * BlockWithoutAnimation.supportsAnimation = false;
   *
   * export default BlockWithoutAnimation;
   */
  supportsAnimation?: boolean;
};

/**
 * Type definition for a blocks factory.
 */

export type BlocksFactory = Record<string, Block>;

/**
 * Factory of all available blocks.
 */

export const blocks: BlocksFactory = {
  // Inline blocks.

  [INLINE_BLOCK_BUNDLE.Card]: CardBlock,
  [INLINE_BLOCK_BUNDLE.Tile]: TileBlock,
  [INLINE_BLOCK_BUNDLE.Title]: TitleBlock,
  [INLINE_BLOCK_BUNDLE.Audio]: AudioBlock,
  [INLINE_BLOCK_BUNDLE.Image]: ImageInlineBlock,
  [INLINE_BLOCK_BUNDLE.ResponsiveImage]: ResponsiveImageBlock,
  [INLINE_BLOCK_BUNDLE.Button]: ButtonBlock,
  [INLINE_BLOCK_BUNDLE.Iframe]: IframeBlock,
  [INLINE_BLOCK_BUNDLE.Spacer]: SpacerBlock,
  [INLINE_BLOCK_BUNDLE.Webform]: WebformBlock,
  [INLINE_BLOCK_BUNDLE.Carousel]: CarouselBlock,
  [INLINE_BLOCK_BUNDLE.RichText]: RichTextBlock,
  [INLINE_BLOCK_BUNDLE.Accordion]: AccordionBlock,
  [INLINE_BLOCK_BUNDLE.LinksList]: LinksList,
  [INLINE_BLOCK_BUNDLE.Separator]: SeparatorBlock,
  [INLINE_BLOCK_BUNDLE.LocalVideo]: LocaleVideoBlock,
  [INLINE_BLOCK_BUNDLE.Navigation]: NavigationBlock,
  [INLINE_BLOCK_BUNDLE.ShareLinks]: ShareLinksBlock,
  [INLINE_BLOCK_BUNDLE.Collapsible]: CollapsibleBlock,
  [INLINE_BLOCK_BUNDLE.RemoteVideo]: RemoteVideoBlock,
  [INLINE_BLOCK_BUNDLE.TexturedTile]: TexturedTileBlock,
  [INLINE_BLOCK_BUNDLE.ClickableTile]: ClickableTileBlock,
  [INLINE_BLOCK_BUNDLE.NestedSection]: NestedSectionBlock,
  [INLINE_BLOCK_BUNDLE.TabbedContent]: TabbedContentBlock,
  [INLINE_BLOCK_BUNDLE.ViewReference]: ViewReferenceBlock,
  [INLINE_BLOCK_BUNDLE.ImagesAccordion]: ImagesAccordionBlock,
  [INLINE_BLOCK_BUNDLE.ScrollableSlide]: ScrollableSlideBlock,
  [INLINE_BLOCK_BUNDLE.ImageReveal]: ImageRevealBlock,
  [INLINE_BLOCK_BUNDLE.AnimatedHeadline]: AnimatedHeadlineBlock,
  [INLINE_BLOCK_BUNDLE.ContentReference]: NodeBlock,
  [INLINE_BLOCK_BUNDLE.BreadCrumbsManual]: BreadCrumbsBlock,
  [INLINE_BLOCK_BUNDLE.ScrollableHeadline]: ScrollableHeadlineBlock,
  [INLINE_BLOCK_BUNDLE.FeatureBand]: FeatureBandBlock,
  [INLINE_BLOCK_BUNDLE.SurveyMonkey]: SurveyMonkeyBlock,

  // Entity blocks.

  [ENTITY_BLOCK_BUNDLE.Node]: NodeBlock,
  [ENTITY_BLOCK_BUNDLE.Term]: TermBlock,

  // Plugin blocks.

  [PLUGIN_BLOCK_BUNDLE.ArticleHeaderMetadata]: ArticleHeaderMetadataBlock,
  [PLUGIN_BLOCK_BUNDLE.InsightSideBar]: InsightSidebarBlock,

  // Field blocks.

  [FIELD_BLOCK_BUNDLE.PageTitle]: PageTitleFieldBlock,
  [FIELD_BLOCK_BUNDLE.PageDescription]: PageDescriptionFieldBlock,
  [FIELD_BLOCK_BUNDLE.PageImage]: PageImageFieldBlock,

  [FIELD_BLOCK_BUNDLE.CampaignPageTitle]: PageTitleFieldBlock,
  [FIELD_BLOCK_BUNDLE.CampaignPageDescription]: PageDescriptionFieldBlock,
  [FIELD_BLOCK_BUNDLE.CampaignPageImage]: PageImageFieldBlock,

  [FIELD_BLOCK_BUNDLE.IntelligenceTitle]: PageTitleFieldBlock,
  [FIELD_BLOCK_BUNDLE.IntelligenceDescription]: PageDescriptionFieldBlock,
  [FIELD_BLOCK_BUNDLE.IntelligenceImage]: PageImageFieldBlock,

  [FIELD_BLOCK_BUNDLE.ArticleTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.ArticleSections]: InsightSectionsBlock,
  [FIELD_BLOCK_BUNDLE.ArticleFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.ArticleInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.ArticleIntroduction]: InsightIntroductionBlock,

  [FIELD_BLOCK_BUNDLE.CaseStudyTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.CaseStudySections]: InsightSectionsBlock,
  [FIELD_BLOCK_BUNDLE.CaseStudyFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.CaseStudyInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.CaseStudyIntroduction]: InsightIntroductionBlock,
  [FIELD_BLOCK_BUNDLE.CaseStudyFileDownloadBtn]: InsightDownloadButton,

  [FIELD_BLOCK_BUNDLE.EventTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.EventSections]: InsightSectionsBlock,
  [FIELD_BLOCK_BUNDLE.EventFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.EventInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.EventIntroduction]: InsightIntroductionBlock,

  [FIELD_BLOCK_BUNDLE.PodcastTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.PodcastSections]: InsightSectionsBlock,
  [FIELD_BLOCK_BUNDLE.PodcastFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.PodcastInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.PodcastIntroduction]: InsightIntroductionBlock,

  [FIELD_BLOCK_BUNDLE.WhitePaperTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.WhitePaperFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.WhitePaperInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.WhitePaperIntroduction]: InsightIntroductionBlock,
  [FIELD_BLOCK_BUNDLE.WhitePaperFileDownloadBtn]: InsightDownloadButton,

  [FIELD_BLOCK_BUNDLE.WebinarTitle]: InsightTitle,
  [FIELD_BLOCK_BUNDLE.WebinarFeaturedImage]: InsightFeaturedImageBlock,
  [FIELD_BLOCK_BUNDLE.WebinarContent]: InsightIntroductionBlock,
  [FIELD_BLOCK_BUNDLE.WebinarInsightsTopics]: InsightTopicsListBlock,
  [FIELD_BLOCK_BUNDLE.WebinarWatchButton]: InsightWebinarWatchButton,

  [FIELD_BLOCK_BUNDLE.SiteAlertTitle]: SiteAlertsTitleFieldBlock,
  [FIELD_BLOCK_BUNDLE.SiteAlertBody]: RichTextBlock,

  // System blocks.

  [SYSTEM_BLOCK_BUNDLE.BreadCrumbs]: BreadCrumbsBlock,
};
